import React, { useCallback, useContext, useEffect, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { IconChevronRight, IconMessageHelp } from '@amzn/stencil-react-components/icons'
import { View } from '@amzn/stencil-react-components/layout'
import { Span } from '@amzn/stencil-react-components/text'

import { APP_CONFIG } from 'src/config.app'
import { ModuleBuilderContext } from 'src/contexts/ModuleBuilderContext'
import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { ModuleService } from 'src/services/backend/ModuleService'
import { AnalysisResponseContent, chatClient, ChatMessage } from 'src/services/ChatService'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'
import { ChatInterface } from './ChatInterface'

interface GHSAuthoringAssistantProps {
    isOpen: boolean
    onToggle: () => void
}

export interface CurrentModuleContext {
    currentModuleVersionId: string
    poolVersionIds: string[]
    itemContainerVersionIds: string[]
}

export const GHSAuthoringAssistant: React.FC<GHSAuthoringAssistantProps> = ({
    isOpen,
    onToggle,
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [analysis, setAnalysis] = useState<AnalysisResponseContent>({} as AnalysisResponseContent)
    const [hasLoadedAnalysis, setHasLoadedAnalysis] = useState(false)
    const apiClient = chatClient
    const moduleBuilderContext = useContext(ModuleBuilderContext)

    const buildModuleContext = useCallback(async (): Promise<{
        context: CurrentModuleContext
        dto: ModuleDTO
    }> => {
        const currentModuleContext: CurrentModuleContext = {
            currentModuleVersionId: moduleBuilderContext?.moduleVersionId,
            poolVersionIds: [],
            itemContainerVersionIds: [],
        }

        const serializedDTO: ModuleDTO = ModuleService.serializeModuleDTO(
            moduleBuilderContext.moduleVersionId
        )
        const currentModuleDTO: ModuleDTO = ModuleService.getFullModuleDTO(serializedDTO)

        if (moduleBuilderContext?.moduleVersionId) {
            const poolVersionIds = currentModuleDTO.workflow
                .filter((activity) => {
                    const moduleChild = activity.moduleChild as Record<string, unknown>
                    return moduleChild.poolVersionId
                })
                .map((activity) => {
                    const moduleChild = activity.moduleChild as Record<string, unknown>
                    return moduleChild.poolVersionId as string
                })

            currentModuleContext.poolVersionIds = poolVersionIds

            const itemPoolMetadataPromises = poolVersionIds
                .filter((versionId) => versionId)
                .map((versionId) => ItemPoolService.getItemPoolMetadata(versionId))

            const itemPoolMetadataResults = await Promise.all(itemPoolMetadataPromises)

            const itemContainerVersionIds: string[] = []
            itemPoolMetadataResults
                .filter((itemPoolMetadata) => itemPoolMetadata)
                .forEach((itemPoolMetadata) => {
                    if (itemPoolMetadata) {
                        itemPoolMetadata.itemContainers?.forEach((itemContainer) => {
                            if (itemContainer.versionId) {
                                itemContainerVersionIds.push(itemContainer.versionId)
                            }
                        })
                    }
                })

            currentModuleContext.itemContainerVersionIds = itemContainerVersionIds
        }

        return { context: currentModuleContext, dto: currentModuleDTO }
    }, [moduleBuilderContext])

    useEffect(() => {
        if (!APP_CONFIG || !APP_CONFIG.auth || !APP_CONFIG.backendAPIBaseUrl) {
            console.error('App configuration is not properly loaded')
            return
        }

        console.log('Chat client initialized')
    }, [])

    const loadAnalysis = useCallback(async () => {
        if (hasLoadedAnalysis || !moduleBuilderContext?.moduleVersionId) return

        setIsLoading(true)
        setMessages([
            { content: 'Analyzing module context...', sender: 'assistant', timestamp: new Date() },
        ])

        try {
            const { context: currentModuleContext, dto: currentModuleDTO } =
                await buildModuleContext()
            const analysisResult = await apiClient.getAnalysis(
                currentModuleContext,
                currentModuleDTO
            )
            setAnalysis(analysisResult)
            setHasLoadedAnalysis(true)
            setMessages([])
        } catch (error) {
            console.error('Error loading analysis:', error)
            setMessages([
                {
                    content: 'Unable to load analysis. Please try again later.',
                    sender: 'assistant',
                    timestamp: new Date(),
                },
            ])
        } finally {
            setIsLoading(false)
        }
    }, [apiClient, buildModuleContext, hasLoadedAnalysis, moduleBuilderContext?.moduleVersionId])

    useEffect(() => {
        if (isOpen && !hasLoadedAnalysis) {
            void loadAnalysis()
        }
    }, [isOpen, loadAnalysis, hasLoadedAnalysis])

    const handleSendMessage = useCallback(
        async (message: string) => {
            if (!apiClient) {
                console.error('API client not initialized')
                return
            }

            const userMessage: ChatMessage = {
                content: message,
                sender: 'user',
                timestamp: new Date(),
            }

            const loadingMessage: ChatMessage = {
                content: 'Thinking...',
                sender: 'assistant',
                timestamp: new Date(),
            }

            setMessages((prevMessages) => [...prevMessages, userMessage, loadingMessage])
            setIsLoading(true)

            try {
                const { context: currentModuleContext, dto: currentModuleDTO } =
                    await buildModuleContext()

                const assistantMessage = await apiClient.sendMessage(
                    message,
                    currentModuleContext,
                    currentModuleDTO,
                    analysis.context_analysis,
                    JSON.stringify(analysis.batch_retrieved_context)
                )

                setMessages((prevMessages) => [...prevMessages.slice(0, -1), assistantMessage])
            } catch (error) {
                console.error('Error sending message:', error)

                const fallbackMessage: ChatMessage = {
                    content:
                        'Sorry, I encountered an error while processing your request. Please try again later.',
                    sender: 'assistant',
                    timestamp: new Date(),
                }

                setMessages((prevMessages) => [...prevMessages.slice(0, -1), fallbackMessage])
            } finally {
                setIsLoading(false)
            }
        },
        [analysis.batch_retrieved_context, analysis.context_analysis, apiClient, buildModuleContext]
    )

    return (
        <>
            <Button
                variant={ButtonVariant.Primary}
                onClick={onToggle}
                aria-label={
                    isOpen ? 'Close GHS Authoring Assistant' : 'Open GHS Authoring Assistant'
                }
                dataTestId='ghs-assistant-toggle'
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '20px',
                    zIndex: 9,
                    boxShadow: '0 4px 12px rgba(51, 35, 35, 0.15)',
                }}
            >
                Authoring Assistant
                {isOpen ? (
                    <IconChevronRight aria-hidden={true} />
                ) : (
                    <IconMessageHelp aria-hidden={true} />
                )}
            </Button>

            <View
                dataTestId='ghs-assistant-container'
                height={'100vh'}
                width={'500px'}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: isOpen ? '0' : '-500px',
                    background: 'white',
                    borderLeft: '1px solid #e0e0e0',
                    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'right 0.3s ease-in-out',
                }}
            >
                <View
                    padding={'S300'}
                    backgroundColor={'#f5f5f5'}
                    style={{
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Span style={{ fontWeight: 'bold' }}>GHS Authoring Assistant</Span>
                    <Button
                        variant={ButtonVariant.Tertiary}
                        onClick={onToggle}
                        aria-label='Close chat'
                        dataTestId='ghs-assistant-close'
                    >
                        <IconChevronRight aria-hidden={true} />
                    </Button>
                </View>
                <View style={{ flex: 1, overflow: 'hidden' }}>
                    <ChatInterface
                        messages={messages}
                        onSendMessage={handleSendMessage}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </>
    )
}
